import React from "react"
import styled, { css } from "styled-components"
import { up } from "styled-breakpoints"
import colors from "@constants/colors"

type TileAlign =
  | "left"
  | "center"
  | "right"
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right"
  | "bottom-center"

export interface HotspotPreviewImage {
  src: string
  alt?: string
}

export interface ImageHotspotItem {
  id: string
  x: number
  y: number
  label: string
  tileAlign?: TileAlign
  previewImage?: HotspotPreviewImage
}

export interface ImageHotspotProps {
  image: {
    src: string
    alt?: string
  }
  hotspots: ImageHotspotItem[]
  className?: string
}

const Section = styled.section`
  width: 100%;
`

const Figure = styled.figure`
  margin: 0;
`

const Canvas = styled.div`
  position: relative;
  overflow: hidden;
  background: transparent;
`

const BaseImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
`

const HotspotAnchor = styled.div<{
  $x: number
  $y: number
  $isActive: boolean
}>`
  ${({ $x, $y, $isActive }) => css`
    position: absolute;
    left: ${$x}%;
    top: ${$y}%;
    transform: translate(-50%, -50%);
    z-index: ${$isActive ? 5 : 2};
  `}
`

const HotspotButton = styled.button<{ $isActive: boolean }>`
  ${({ $isActive }) => css`
    position: relative;
    width: 30px;
    height: 30px;
    border: 3px solid
      ${$isActive ? "rgba(255, 70, 58, 0.95)" : "rgba(215, 215, 215, 0.95)"};
    border-radius: 999px;
    padding: 0;
    cursor: pointer;
    background: ${$isActive
      ? "rgba(172, 21, 14, 0.82)"
      : "rgba(90, 88, 80, 0.78)"};
    box-shadow: ${$isActive
      ? "0 12px 22px rgba(160, 25, 18, 0.28)"
      : "0 10px 18px rgba(0, 0, 0, 0.18)"};
    transition: transform 160ms ease, box-shadow 160ms ease, opacity 160ms ease,
      border-color 160ms ease, background 160ms ease;
    opacity: ${$isActive ? 1 : 0.96};

    &::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      width: 14px;
      height: 14px;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.98);
      transform: translate(-50%, -50%);
    }

    &:hover,
    &:focus-visible {
      transform: scale(1.03);
      box-shadow: 0 12px 22px rgba(160, 25, 18, 0.28);
      border-color: rgba(255, 70, 58, 0.95);
      background: rgba(172, 21, 14, 0.82);
      outline: none;
    }
  `}
`

const TileAnchor = styled.button<{
  $align: TileAlign
  $isActive: boolean
  $nudgeX: number
  $nudgeY: number
}>`
  ${({ $align, $isActive, $nudgeX, $nudgeY }) => css`
    position: absolute;
    z-index: 3;
    border: 0;
    padding: 0;
    background: transparent;
    cursor: pointer;
    color: ${colors.snow};
    text-align: center;
    pointer-events: ${$isActive ? "auto" : "none"};
    visibility: ${$isActive ? "visible" : "hidden"};

    ${$align === "left" &&
    css`
      right: calc(100% + 10px);
      top: 50%;
      transform: translate(${$nudgeX}px, calc(-50% + ${$nudgeY}px));
    `}

    ${$align === "right" &&
    css`
      left: calc(100% + 10px);
      top: 50%;
      transform: translate(${$nudgeX}px, calc(-50% + ${$nudgeY}px));
    `}

    ${$align === "center" &&
    css`
      left: 50%;
      bottom: calc(100% + 10px);
      transform: translate(calc(-50% + ${$nudgeX}px), ${$nudgeY}px);
    `}

    ${$align === "top-left" &&
    css`
      right: calc(100% + 10px);
      bottom: calc(100% + 10px);
      transform: translate(${$nudgeX}px, ${$nudgeY}px);
    `}

    ${$align === "top-right" &&
    css`
      left: calc(100% + 10px);
      bottom: calc(100% + 10px);
      transform: translate(${$nudgeX}px, ${$nudgeY}px);
    `}

    ${$align === "bottom-left" &&
    css`
      right: calc(100% + 10px);
      top: calc(100% + 10px);
      transform: translate(${$nudgeX}px, ${$nudgeY}px);
    `}

    ${$align === "bottom-right" &&
    css`
      left: calc(100% + 10px);
      top: calc(100% + 10px);
      transform: translate(${$nudgeX}px, ${$nudgeY}px);
    `}

    ${$align === "bottom-center" &&
    css`
      left: 50%;
      top: calc(100% + 10px);
      transform: translate(calc(-50% + ${$nudgeX}px), ${$nudgeY}px);
    `}

    &:focus-visible {
      outline: none;
    }

    .tile-content {
      opacity: ${$isActive ? 1 : 0};
      transition: opacity 180ms ease;
    }

    .tile-frame {
      transform: none;
      box-shadow: ${$isActive
        ? "0 14px 28px rgba(255, 0, 0, 0.2)"
        : "0 10px 20px rgba(0, 0, 0, 0.22)"};
      transition: box-shadow 180ms ease, opacity 180ms ease;
    }

    .tile-label {
      color: ${$isActive ? colors.snow : "rgba(255, 255, 255, 0.88)"};
      transition: color 180ms ease;
    }
  `}
`

const TileContent = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

const TileLabel = styled.span`
  position: absolute;
  left: 50%;
  bottom: calc(100% + 8px);
  transform: translateX(-50%);
  display: block;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;

  ${up("tablet")} {
    font-size: 21px;
  }
`

const TileFrame = styled.span<{ $hasImage: boolean }>`
  ${({ $hasImage }) => css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${$hasImage ? "64px" : "0"};
    height: ${$hasImage ? "64px" : "0"};
    min-width: ${$hasImage ? "64px" : "0"};
    border-radius: 10px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.14);
    border: ${$hasImage ? "3px solid #ff2f1d" : "0"};
    transition: transform 160ms ease, opacity 160ms ease, box-shadow 160ms ease;

    ${up("tablet")} {
      width: ${$hasImage ? "140px" : "0"};
      height: ${$hasImage ? "140px" : "0"};
      min-width: ${$hasImage ? "140px" : "0"};
    }
  `}
`

const TileImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const ImageHotspot: React.FC<ImageHotspotProps> = ({
  image,
  hotspots,
  className,
}) => {
  const containerRef = React.useRef<HTMLElement>(null)
  const canvasRef = React.useRef<HTMLDivElement>(null)
  const tileRefs = React.useRef<Record<string, HTMLButtonElement | null>>({})
  const [activeId, setActiveId] = React.useState<string | null>(null)
  const [tileNudges, setTileNudges] = React.useState<
    Record<string, { x: number; y: number }>
  >({})

  const activate = (id: string) => setActiveId(id)
  const clear = () => setActiveId(null)
  const measureTile = React.useCallback((id: string) => {
    const canvasEl = canvasRef.current
    const tileEl = tileRefs.current[id]

    if (!canvasEl || !tileEl) return

    const canvasRect = canvasEl.getBoundingClientRect()
    const tileRect = tileEl.getBoundingClientRect()
    const padding = 8

    setTileNudges((current) => {
      let nudgeX = current[id]?.x || 0
      let nudgeY = current[id]?.y || 0

      if (tileRect.left < canvasRect.left + padding) {
        nudgeX += canvasRect.left + padding - tileRect.left
      }

      if (tileRect.right > canvasRect.right - padding) {
        nudgeX -= tileRect.right - (canvasRect.right - padding)
      }

      if (tileRect.top < canvasRect.top + padding) {
        nudgeY += canvasRect.top + padding - tileRect.top
      }

      if (tileRect.bottom > canvasRect.bottom - padding) {
        nudgeY -= tileRect.bottom - (canvasRect.bottom - padding)
      }

      const prev = current[id]
      if (prev?.x === nudgeX && prev?.y === nudgeY) return current
      return {
        ...current,
        [id]: { x: nudgeX, y: nudgeY },
      }
    })
  }, [])

  React.useEffect(() => {
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        clear()
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        clear()
      }
    }

    document.addEventListener("mousedown", handlePointerDown)
    document.addEventListener("touchstart", handlePointerDown)
    document.addEventListener("keydown", handleKeyDown)

    return () => {
      document.removeEventListener("mousedown", handlePointerDown)
      document.removeEventListener("touchstart", handlePointerDown)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [])

  React.useLayoutEffect(() => {
    if (!activeId || !canvasRef.current) return

    const tileEl = tileRefs.current[activeId]
    if (!tileEl) return

    let frameId = 0
    let settleFrameId = 0

    const measure = () => measureTile(activeId)

    measure()
    frameId = window.requestAnimationFrame(() => {
      settleFrameId = window.requestAnimationFrame(measure)
    })

    const resizeObserver = new ResizeObserver(measure)
    resizeObserver.observe(tileEl)
    resizeObserver.observe(canvasRef.current)
    window.addEventListener("resize", measure)

    return () => {
      window.cancelAnimationFrame(frameId)
      window.cancelAnimationFrame(settleFrameId)
      resizeObserver.disconnect()
      window.removeEventListener("resize", measure)
    }
  }, [activeId, measureTile])

  return (
    <Section className={className} ref={containerRef}>
      <Figure>
        <Canvas ref={canvasRef}>
          <BaseImage src={image.src} alt={image.alt || ""} />
          {hotspots.map((hotspot) => {
            const isActive = activeId === hotspot.id
            const resolvedAlign = hotspot.tileAlign || "right"
            const nudge = tileNudges[hotspot.id] || { x: 0, y: 0 }

            return (
              <HotspotAnchor
                key={hotspot.id}
                $x={hotspot.x}
                $y={hotspot.y}
                $isActive={isActive}
              >
                <HotspotButton
                  type="button"
                  aria-label={`Select ${hotspot.label}`}
                  aria-pressed={isActive}
                  $isActive={isActive}
                  onMouseEnter={() => activate(hotspot.id)}
                  onMouseLeave={clear}
                  onFocus={() => activate(hotspot.id)}
                  onClick={() => activate(hotspot.id)}
                />
                <TileAnchor
                  type="button"
                  aria-label={`Preview ${hotspot.label}`}
                  $align={resolvedAlign}
                  $isActive={isActive}
                  $nudgeX={nudge.x}
                  $nudgeY={nudge.y}
                  ref={(node) => {
                    tileRefs.current[hotspot.id] = node
                  }}
                  onFocus={() => activate(hotspot.id)}
                  onClick={() => activate(hotspot.id)}
                >
                  <TileContent className="tile-content">
                    <TileLabel className="tile-label">
                      {hotspot.label}
                    </TileLabel>
                    <TileFrame
                      className="tile-frame"
                      $hasImage={Boolean(hotspot.previewImage)}
                    >
                      {hotspot.previewImage && (
                        <TileImage
                          src={hotspot.previewImage.src}
                          alt={hotspot.previewImage.alt || hotspot.label}
                          onLoad={() => {
                            if (activeId === hotspot.id) {
                              window.requestAnimationFrame(() => {
                                measureTile(hotspot.id)
                              })
                            }
                          }}
                        />
                      )}
                    </TileFrame>
                  </TileContent>
                </TileAnchor>
              </HotspotAnchor>
            )
          })}
        </Canvas>
      </Figure>
    </Section>
  )
}

export default ImageHotspot
